import React, { useState, useEffect } from 'react';
import { Card, Tabs, Row, Col, Statistic, Spin, Typography, Space, Button, Select } from 'antd';
import { 
  UserOutlined, 
  TrophyOutlined, 
  ExclamationCircleOutlined, 
  BookOutlined,
  StarOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { schoolAPI } from '../../services/api';
import { normalizeScopeId, resolveDefaultSchoolId, shouldLockSchoolSelection } from '../../utils/schoolScope';

// 导入各个tab组件
import StudentList from './StudentList';

const { Title } = Typography;
const { Option } = Select;

const StudentManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    totalStudents: 0,
    totalPoints: 0,
    totalViolations: 0,
    totalHomework: 0,
    totalGrades: 0,
    totalPraise: 0,
    activeStudents: 0,
    averageGrade: 0
  });

  // 获取仪表盘数据
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const schoolId = selectedSchoolId;
      if (!schoolId) {
        setLoading(false);
        return;
      }
      
      // 并行获取所有统计数据
      const [studentsRes, pointsRes, violationsRes, praiseRes] = await Promise.all([
        api.get('/students', { params: { schoolId, limit: 1 } }).catch(() => ({ data: { data: [], pagination: { total: 0 }, summary: { activeStudents: 0 } } })),
        api.get(`/student-points/school-stats/${schoolId}`).catch(() => ({ data: { netPoints: 0 } })),
        api.get('/student-violations', { params: { schoolId, limit: 1 } }).catch(() => ({ data: { data: [], pagination: { total: 0 } } })),
        api.get('/student-praise', { params: { schoolId, limit: 1 } }).catch(() => ({ data: { data: [], pagination: { total: 0 } } }))
      ]);
      
      const totalStudents = studentsRes.data.summary?.totalStudents || studentsRes.data.pagination?.total || 0;
      const activeStudents = studentsRes.data.summary?.activeStudents || 0;
      const totalPoints = pointsRes.data?.data?.netPoints ?? pointsRes.data?.netPoints ?? 0;
      const totalViolations = violationsRes.data.pagination?.total || 0;
      const totalPraise = praiseRes.data.pagination?.total || 0;
      
      setDashboardData({
        totalStudents,
        totalPoints,
        totalViolations,
        totalHomework: 0, // 需要添加作业API
        totalGrades: 0, // 需要添加成绩API
        totalPraise,
        activeStudents,
        averageGrade: 0 // 需要计算平均成绩
      });
    } catch (error) {
      console.error('获取仪表盘数据失败:', error);
      // 失败时显示0
      setDashboardData({
        totalStudents: 0,
        totalPoints: 0,
        totalViolations: 0,
        totalHomework: 0,
        totalGrades: 0,
        totalPraise: 0,
        activeStudents: 0,
        averageGrade: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    schoolAPI.getAll().then((response) => {
      const rows = response.data?.data || [];
      const availableSchools = user?.workbenchSchoolId ? rows.filter(s => String(s.id) === user.workbenchSchoolId) : rows;
      setSchools(availableSchools);
      setSelectedSchoolId(resolveDefaultSchoolId(user, availableSchools));
    }).catch((error) => {
      console.error('获取授权学校失败:', error);
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (selectedSchoolId) fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolId]);

  const handleRefreshData = () => {
    fetchDashboardData();
  };

  // 仪表盘统计卡片
  const renderDashboard = () => (
    <Card 
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>学生管理数据概览</Title>
          <Select
            value={selectedSchoolId}
            onChange={setSelectedSchoolId}
            disabled={shouldLockSchoolSelection(user, schools)}
            placeholder="选择授权学校"
            style={{ width: 240 }}
          >
            {schools.map(school => (
              <Option key={school.id} value={normalizeScopeId(school.id)}>{school.name}</Option>
            ))}
          </Select>
          <Button 
            type="link" 
            icon={<ReloadOutlined />} 
            onClick={handleRefreshData}
            loading={loading}
          >
            刷新数据
          </Button>
        </Space>
      }
      style={{ marginBottom: '24px' }}
    >
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="学生总数"
                value={dashboardData.totalStudents}
                prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
                suffix="人"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="活跃学生"
                value={dashboardData.activeStudents}
                prefix={<UserOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
                suffix="人"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="学生积分"
                value={dashboardData.totalPoints}
                prefix={<TrophyOutlined style={{ color: '#fa8c16' }} />}
                valueStyle={{ color: '#fa8c16' }}
                suffix="分"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="平均成绩"
                value={dashboardData.averageGrade}
                precision={1}
                prefix={<StarOutlined style={{ color: '#722ed1' }} />}
                valueStyle={{ color: '#722ed1' }}
                suffix="分"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="作业总数"
                value={dashboardData.totalHomework}
                prefix={<BookOutlined style={{ color: '#13c2c2' }} />}
                valueStyle={{ color: '#13c2c2' }}
                suffix="份"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="表扬记录"
                value={dashboardData.totalPraise}
                prefix={<StarOutlined style={{ color: '#eb2f96' }} />}
                valueStyle={{ color: '#eb2f96' }}
                suffix="次"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="违纪记录"
                value={dashboardData.totalViolations}
                prefix={<ExclamationCircleOutlined style={{ color: '#f5222d' }} />}
                valueStyle={{ color: '#f5222d' }}
                suffix="次"
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </Card>
  );

  return (
    <div style={{ padding: '24px' }}>
      {renderDashboard()}
      
      <Card title="学生资料">
        <StudentList />
      </Card>
    </div>
  );
};

export default StudentManagement;
